/**
 * Generic render error boundary. Catches errors thrown while rendering its
 * subtree and shows a compact fallback instead of unmounting the whole app, so
 * (for example) a graph that fails to lay out can't take the surrounding chat
 * and "save" controls down with it.
 *
 * Pass `resetKey` (any value that changes when the inputs change, e.g. the set's
 * agent count) and the boundary clears its error when that key changes, letting
 * the next, good render through.
 */
import { Component } from 'react';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, info) {
    // Surface the React error for diagnosis; the fallback shows the message too.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info?.componentStack);
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (typeof this.props.fallback === 'function') return this.props.fallback(error, this.reset);
    return (
      <Sheet
        variant="soft"
        color="danger"
        sx={{ p: 2, borderRadius: 'sm', height: '100%', minHeight: 160, display: 'flex' }}
      >
        <Stack spacing={1} sx={{ m: 'auto', alignItems: 'center', textAlign: 'center', maxWidth: 380 }}>
          <ReportProblemIcon color="danger" />
          <Typography level="title-sm" color="danger">
            {this.props.title || 'Something went wrong rendering this view.'}
          </Typography>
          <Typography level="body-xs" sx={{ color: 'text.tertiary', wordBreak: 'break-word' }}>
            {String(error?.message || error)}
          </Typography>
          <Button size="sm" variant="outlined" color="danger" onClick={this.reset}>
            Try again
          </Button>
        </Stack>
      </Sheet>
    );
  }
}
